import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DollarSign,
  GraduationCap,
  Users,
  FileText,
  Plus,
  Download,
  Upload,
  Trash2,
  Edit,
  Star,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Briefcase,
  File,
  Send,
  Eye,
  Stethoscope,
  UserPlus,
  AlertTriangle,
  FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import {
  JobApplicationsTab,
  PayrollTab,
  EvaluationsTab,
  PCWEvaluationsTab,
  RNEvaluationsTab,
  DisciplinaryActionsTab,
  PayrollExportTab,
  PayrollDialog,
  EvaluationDialog,
  PCWEvaluationDialog,
  RNEvaluationDialog,
  TrainingHoursTab,
} from "@/components/hr-payroll";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const payPeriodOptions = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "semimonthly", label: "Semi-Monthly" },
  { value: "monthly", label: "Monthly" },
];

const evaluationRatings = [
  { value: 5, label: "Excellent", color: "text-emerald-600" },
  { value: 4, label: "Good", color: "text-blue-600" },
  { value: 3, label: "Satisfactory", color: "text-amber-600" },
  { value: 2, label: "Needs Improvement", color: "text-orange-600" },
  { value: 1, label: "Unsatisfactory", color: "text-red-600" },
];

export default function HRPayrollPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [jobApplications, setJobApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Payroll Dialog State
  const [payrollDialogOpen, setPayrollDialogOpen] = useState(false);
  const [savingPayroll, setSavingPayroll] = useState(false);
  const [newPayroll, setNewPayroll] = useState({
    employee_id: "",
    pay_period_start: "",
    pay_period_end: "",
    hours_worked: "",
    hourly_rate: "",
    overtime_hours: "",
    overtime_rate: "",
    deductions: "",
    bonuses: "",
    notes: "",
  });

  // Evaluation Dialog State
  const [evalDialogOpen, setEvalDialogOpen] = useState(false);
  const [savingEval, setSavingEval] = useState(false);
  const [newEvaluation, setNewEvaluation] = useState({
    employee_id: "",
    evaluation_date: "",
    evaluator_name: "",
    period_start: "",
    period_end: "",
    attendance_rating: 3,
    punctuality_rating: 3,
    quality_of_work_rating: 3,
    communication_rating: 3,
    teamwork_rating: 3,
    professionalism_rating: 3,
    strengths: "",
    areas_for_improvement: "",
    goals: "",
    overall_comments: "",
  });

  // PCW Evaluation State
  const [pcwEvalDialogOpen, setPcwEvalDialogOpen] = useState(false);
  const [savingPcwEval, setSavingPcwEval] = useState(false);
  const [pcwEvaluations, setPcwEvaluations] = useState([]);
  const [pcwDuties, setPcwDuties] = useState([]);
  const [pcwGradingScale, setPcwGradingScale] = useState([]);
  const [newPcwEvaluation, setNewPcwEvaluation] = useState({
    employee_id: "",
    employee_name: "",
    evaluation_date: new Date().toISOString().split("T")[0],
    evaluator_name: "",
    supervisor_name: "",
    period_start: "",
    period_end: "",
    duty_1_hygiene_environment: 3,
    duty_2_meals_marketing: 3,
    duty_3_ambulation: 3,
    duty_4_independence: 3,
    duty_5_mental_alertness: 3,
    duty_6_emotional_support: 3,
    duty_7_visit_reports: 3,
    duty_8_condition_reporting: 3,
    duty_9_scope_of_practice: 3,
    duty_10_emergency_procedures: 3,
    goal_1: "",
    goal_2: "",
    goal_3: "",
    additional_comments: "",
    employee_comments: "",
  });

  // Document upload state
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // RN Evaluation State (Admin Only)
  const [rnEvalDialogOpen, setRnEvalDialogOpen] = useState(false);
  const [savingRnEval, setSavingRnEval] = useState(false);
  const [rnEvaluations, setRnEvaluations] = useState([]);
  const [rnDuties, setRnDuties] = useState([]);
  const [rnGradingScale, setRnGradingScale] = useState([]);
  const [newRnEvaluation, setNewRnEvaluation] = useState({
    employee_id: "",
    employee_name: "",
    evaluation_date: new Date().toISOString().split("T")[0],
    period_start: "",
    period_end: "",
    duty_1_scheduling: 3,
    duty_2_pcw_supervision: 3,
    duty_3_clinical_review: 3,
    duty_4_community_resources: 3,
    duty_5_staff_appraisal: 3,
    duty_6_community_functions: 3,
    duty_7_orientation: 3,
    duty_8_home_visits: 3,
    duty_9_record_reviews: 3,
    duty_10_schedule_review: 3,
    duty_11_paperwork: 3,
    duty_12_continuing_education: 3,
    duty_13_other_duties: 3,
    goals: "",
    additional_comments: "",
  });

  useEffect(() => {
    if (user?.role !== "admin") {
      toast.error("Access denied. Administrator only.");
      navigate("/dashboard");
      return;
    }
    fetchData();
    fetchPcwData();
    fetchRnData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employeesRes, payrollRes, evalRes, applicationsRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/hr/payroll`),
        axios.get(`${API}/hr/evaluations`),
        axios.get(`${API}/public/agency/${user?.agency_code}/job-applications`).catch(() => ({ data: [] })),
      ]);
      setEmployees(employeesRes.data);
      setPayrollRecords(payrollRes.data);
      setEvaluations(evalRes.data);
      setJobApplications(applicationsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch HR data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPcwData = async () => {
    try {
      const [dutiesRes, evalsRes] = await Promise.all([
        axios.get(`${API}/hr/pcw-evaluations/duties`),
        axios.get(`${API}/hr/pcw-evaluations`),
      ]);
      setPcwDuties(dutiesRes.data.duties || []);
      setPcwGradingScale(dutiesRes.data.grading_scale || []);
      setPcwEvaluations(evalsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch PCW data:", error);
    }
  };

  const fetchRnData = async () => {
    try {
      const [dutiesRes, evalsRes] = await Promise.all([
        axios.get(`${API}/hr/rn-evaluations/duties`),
        axios.get(`${API}/hr/rn-evaluations`),
      ]);
      setRnDuties(dutiesRes.data.duties || []);
      setRnGradingScale(dutiesRes.data.grading_scale || []);
      setRnEvaluations(evalsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch RN data:", error);
    }
  };

  const handleSaveRnEvaluation = async () => {
    if (!newRnEvaluation.employee_id) {
      toast.error("Please select an employee");
      return;
    }
    
    setSavingRnEval(true);
    try {
      await axios.post(`${API}/hr/rn-evaluations`, newRnEvaluation);
      toast.success("RN evaluation saved successfully");
      setRnEvalDialogOpen(false);
      fetchRnData();
      // Reset form
      setNewRnEvaluation({
        employee_id: "",
        employee_name: "",
        evaluation_date: new Date().toISOString().split("T")[0],
        period_start: "",
        period_end: "",
        duty_1_scheduling: 3,
        duty_2_pcw_supervision: 3,
        duty_3_clinical_review: 3,
        duty_4_community_resources: 3,
        duty_5_staff_appraisal: 3,
        duty_6_community_functions: 3,
        duty_7_orientation: 3,
        duty_8_home_visits: 3,
        duty_9_record_reviews: 3,
        duty_10_schedule_review: 3,
        duty_11_paperwork: 3,
        duty_12_continuing_education: 3,
        duty_13_other_duties: 3,
        goals: "",
        additional_comments: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save RN evaluation");
    } finally {
      setSavingRnEval(false);
    }
  };

  const handleExportRnPdf = async (evalId) => {
    try {
      const response = await axios.get(`${API}/hr/rn-evaluations/${evalId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `RN_Evaluation_${evalId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const handleCompleteRnEvaluation = async (evalId, employeeName) => {
    if (!confirm(`Mark this evaluation as complete for ${employeeName}?`)) {
      return;
    }
    
    try {
      await axios.patch(`${API}/hr/rn-evaluations/${evalId}/complete`);
      toast.success("RN evaluation marked as complete");
      fetchRnData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete evaluation");
    }
  };

  const handleDeleteRnEvaluation = async (evalId, employeeName) => {
    if (!confirm(`Delete evaluation for ${employeeName}? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/hr/rn-evaluations/${evalId}`);
      toast.success("RN evaluation deleted");
      fetchRnData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete evaluation");
    }
  };

  const handleSavePcwEvaluation = async () => {
    if (!newPcwEvaluation.employee_id || !newPcwEvaluation.evaluator_name) {
      toast.error("Please select an employee and enter evaluator name");
      return;
    }

    setSavingPcwEval(true);
    try {
      await axios.post(`${API}/hr/pcw-evaluations`, newPcwEvaluation);
      toast.success("PCW evaluation saved!");
      setPcwEvalDialogOpen(false);
      fetchPcwData();
      // Reset form
      setNewPcwEvaluation({
        employee_id: "",
        employee_name: "",
        evaluation_date: new Date().toISOString().split("T")[0],
        evaluator_name: user?.name || "",
        supervisor_name: "",
        period_start: "",
        period_end: "",
        duty_1_hygiene_environment: 3,
        duty_2_meals_marketing: 3,
        duty_3_ambulation: 3,
        duty_4_independence: 3,
        duty_5_mental_alertness: 3,
        duty_6_emotional_support: 3,
        duty_7_visit_reports: 3,
        duty_8_condition_reporting: 3,
        duty_9_scope_of_practice: 3,
        duty_10_emergency_procedures: 3,
        goal_1: "",
        goal_2: "",
        goal_3: "",
        additional_comments: "",
        employee_comments: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save evaluation");
    } finally {
      setSavingPcwEval(false);
    }
  };

  const getGradeColor = (grade) => {
    const colors = {
      1: "bg-emerald-100 text-emerald-800",
      2: "bg-green-100 text-green-800",
      3: "bg-yellow-100 text-yellow-800",
      4: "bg-orange-100 text-orange-800",
      5: "bg-red-100 text-red-800",
    };
    return colors[grade] || "bg-slate-100 text-slate-800";
  };

  const getGradeLabel = (grade) => {
    const labels = { 1: "Excellent", 2: "Very Good", 3: "Average", 4: "Below Average", 5: "Poor" };
    return labels[grade] || "N/A";
  };

  const handleExportPcwPdf = async (evalId) => {
    try {
      const response = await axios.get(`${API}/hr/pcw-evaluations/${evalId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PCW_Evaluation_${evalId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const handleCompleteAndSend = async (evalId, employeeName) => {
    if (!confirm(`Complete this evaluation and send to ${employeeName} for signature?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/hr/pcw-evaluations/${evalId}/complete-and-send`);
      toast.success(response.data.message);
      fetchPcwData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete and send evaluation");
    }
  };

  const handleResendEvaluation = async (evalId, employeeName) => {
    if (!confirm(`Resend evaluation notification to ${employeeName}?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/hr/pcw-evaluations/${evalId}/resend`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend evaluation");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: "bg-slate-100 text-slate-700", label: "Draft" },
      completed: { color: "bg-emerald-100 text-emerald-700", label: "Completed" },
      pending_signature: { color: "bg-amber-100 text-amber-700", label: "Pending Signature" },
      acknowledged: { color: "bg-blue-100 text-blue-700", label: "Acknowledged" },
    };
    return badges[status] || badges.draft;
  };

  const calculateGrossPay = (record) => {
    const regular = (record.hours_worked || 0) * (record.hourly_rate || 0);
    const overtime = (record.overtime_hours || 0) * (record.overtime_rate || 0);
    return regular + overtime + (record.bonuses || 0);
  };

  const calculateNetPay = (record) => {
    return calculateGrossPay(record) - (record.deductions || 0);
  };

  const handleCreatePayroll = async (e) => {
    e.preventDefault();
    if (!newPayroll.employee_id || !newPayroll.pay_period_start || !newPayroll.pay_period_end) {
      toast.error("Please fill in required fields");
      return;
    }

    setSavingPayroll(true);
    try {
      await axios.post(`${API}/hr/payroll`, {
        ...newPayroll,
        hours_worked: parseFloat(newPayroll.hours_worked) || 0,
        hourly_rate: parseFloat(newPayroll.hourly_rate) || 0,
        overtime_hours: parseFloat(newPayroll.overtime_hours) || 0,
        overtime_rate: parseFloat(newPayroll.overtime_rate) || 0,
        deductions: parseFloat(newPayroll.deductions) || 0,
        bonuses: parseFloat(newPayroll.bonuses) || 0,
      });
      toast.success("Payroll record created!");
      setPayrollDialogOpen(false);
      setNewPayroll({
        employee_id: "",
        pay_period_start: "",
        pay_period_end: "",
        hours_worked: "",
        hourly_rate: "",
        overtime_hours: "",
        overtime_rate: "",
        deductions: "",
        bonuses: "",
        notes: "",
      });
      fetchData();
    } catch (error) {
      console.error("Failed to create payroll:", error);
      toast.error("Failed to create payroll record");
    } finally {
      setSavingPayroll(false);
    }
  };

  const handleCreateEvaluation = async (e) => {
    e.preventDefault();
    if (!newEvaluation.employee_id || !newEvaluation.evaluation_date) {
      toast.error("Please select employee and evaluation date");
      return;
    }

    setSavingEval(true);
    try {
      await axios.post(`${API}/hr/evaluations`, newEvaluation);
      toast.success("Performance evaluation saved!");
      setEvalDialogOpen(false);
      setNewEvaluation({
        employee_id: "",
        evaluation_date: "",
        evaluator_name: user?.name || "",
        period_start: "",
        period_end: "",
        attendance_rating: 3,
        punctuality_rating: 3,
        quality_of_work_rating: 3,
        communication_rating: 3,
        teamwork_rating: 3,
        professionalism_rating: 3,
        strengths: "",
        areas_for_improvement: "",
        goals: "",
        overall_comments: "",
      });
      fetchData();
    } catch (error) {
      console.error("Failed to create evaluation:", error);
      toast.error("Failed to save evaluation");
    } finally {
      setSavingEval(false);
    }
  };

  const handleDeletePayroll = async (id) => {
    if (!window.confirm("Delete this payroll record?")) return;
    try {
      await axios.delete(`${API}/hr/payroll/${id}`);
      toast.success("Payroll record deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteEvaluation = async (id) => {
    if (!window.confirm("Delete this evaluation?")) return;
    try {
      await axios.delete(`${API}/hr/evaluations/${id}`);
      toast.success("Evaluation deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleUploadDocument = async (evalId) => {
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    setUploadingDoc(evalId);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      await axios.post(`${API}/hr/evaluations/${evalId}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Document uploaded!");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchData();
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploadingDoc(null);
    }
  };

  const getAverageRating = (eval_) => {
    const ratings = [
      eval_.attendance_rating,
      eval_.punctuality_rating,
      eval_.quality_of_work_rating,
      eval_.communication_rating,
      eval_.teamwork_rating,
      eval_.professionalism_rating,
    ];
    return (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1);
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return "text-emerald-600 bg-emerald-50";
    if (rating >= 3.5) return "text-blue-600 bg-blue-50";
    if (rating >= 2.5) return "text-amber-600 bg-amber-50";
    return "text-red-600 bg-red-50";
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp?.name || "Unknown";
  };

  if (user?.role !== "admin") {
    return null;
  }

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">HR & Payroll</h1>
        <p className="text-slate-500">Manage employee payroll and performance evaluations</p>
      </div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "applications"} className="space-y-6">
        <TabsList className="bg-white border border-slate-200 flex-wrap">
          <TabsTrigger value="applications" className="gap-2" data-testid="tab-applications">
            <UserPlus className="w-4 h-4" />
            Job Applications
          </TabsTrigger>
          <TabsTrigger value="payroll" className="gap-2" data-testid="tab-payroll">
            <DollarSign className="w-4 h-4" />
            Payroll
          </TabsTrigger>
          <TabsTrigger value="payroll-export" className="gap-2" data-testid="tab-payroll-export">
            <FileSpreadsheet className="w-4 h-4" />
            Export
          </TabsTrigger>
          <TabsTrigger value="disciplinary" className="gap-2" data-testid="tab-disciplinary">
            <AlertTriangle className="w-4 h-4" />
            Disciplinary
          </TabsTrigger>
          <TabsTrigger value="evaluations" className="gap-2" data-testid="tab-evaluations">
            <Star className="w-4 h-4" />
            Performance Evaluations
          </TabsTrigger>
          <TabsTrigger value="pcw-evaluations" className="gap-2" data-testid="tab-pcw-evaluations">
            <FileText className="w-4 h-4" />
            PCW Evaluations
          </TabsTrigger>
          <TabsTrigger value="rn-evaluations" className="gap-2" data-testid="tab-rn-evaluations">
            <Stethoscope className="w-4 h-4" />
            RN Evaluations
          </TabsTrigger>
          <TabsTrigger value="training-hours" className="gap-2" data-testid="tab-training-hours">
            <GraduationCap className="w-4 h-4" />
            Training Hours
          </TabsTrigger>
        </TabsList>

        {/* Job Applications Tab */}
        <TabsContent value="applications">
          <JobApplicationsTab 
            applications={jobApplications}
            loading={loading}
            onRefresh={fetchData}
          />
        </TabsContent>

        {/* Payroll Tab */}
        <TabsContent value="payroll">
          <PayrollTab
            payrollRecords={payrollRecords}
            loading={loading}
            onAddClick={() => setPayrollDialogOpen(true)}
            onDelete={handleDeletePayroll}
            getEmployeeName={getEmployeeName}
            calculateGrossPay={calculateGrossPay}
            calculateNetPay={calculateNetPay}
          />
        </TabsContent>

        {/* Payroll Export Tab */}
        <TabsContent value="payroll-export">
          <PayrollExportTab />
        </TabsContent>

        {/* Disciplinary Actions Tab */}
        <TabsContent value="disciplinary">
          <DisciplinaryActionsTab employees={employees} />
        </TabsContent>

        {/* Performance Evaluations Tab */}
        <TabsContent value="evaluations">
          <EvaluationsTab
            evaluations={evaluations}
            loading={loading}
            onAddClick={() => {
              setNewEvaluation(prev => ({ ...prev, evaluator_name: user?.name || "" }));
              setEvalDialogOpen(true);
            }}
            onDelete={handleDeleteEvaluation}
            onUploadDocument={handleUploadDocument}
            uploadingDoc={uploadingDoc}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            getEmployeeName={getEmployeeName}
            getAverageRating={getAverageRating}
            getRatingColor={getRatingColor}
          />
        </TabsContent>

        {/* PCW Evaluations Tab */}
        <TabsContent value="pcw-evaluations">
          <PCWEvaluationsTab
            pcwEvaluations={pcwEvaluations}
            pcwGradingScale={pcwGradingScale}
            pcwDuties={pcwDuties}
            onAddClick={() => {
              setNewPcwEvaluation(prev => ({ ...prev, evaluator_name: user?.name || "" }));
              setPcwEvalDialogOpen(true);
            }}
            onExportPdf={handleExportPcwPdf}
            onCompleteAndSend={handleCompleteAndSend}
            onResend={handleResendEvaluation}
            getGradeColor={getGradeColor}
            getGradeLabel={getGradeLabel}
          />
        </TabsContent>

        {/* RN Evaluations Tab (Admin Only) */}
        <TabsContent value="rn-evaluations">
          <RNEvaluationsTab
            rnEvaluations={rnEvaluations}
            rnDuties={rnDuties}
            onAddClick={() => setRnEvalDialogOpen(true)}
            onExportPdf={handleExportRnPdf}
            onComplete={handleCompleteRnEvaluation}
            onDelete={handleDeleteRnEvaluation}
            getGradeColor={getGradeColor}
            getGradeLabel={getGradeLabel}
          />
        </TabsContent>

        <TabsContent value="training-hours">
          <TrainingHoursTab />
        </TabsContent>
      </Tabs>

      {/* Extracted Dialog Components */}
      <PayrollDialog
        open={payrollDialogOpen}
        onOpenChange={setPayrollDialogOpen}
        employees={employees}
        newPayroll={newPayroll}
        setNewPayroll={setNewPayroll}
        saving={savingPayroll}
        onSubmit={handleCreatePayroll}
      />

      <EvaluationDialog
        open={evalDialogOpen}
        onOpenChange={setEvalDialogOpen}
        employees={employees}
        newEvaluation={newEvaluation}
        setNewEvaluation={setNewEvaluation}
        saving={savingEval}
        onSubmit={handleCreateEvaluation}
      />

      <PCWEvaluationDialog
        open={pcwEvalDialogOpen}
        onOpenChange={setPcwEvalDialogOpen}
        employees={employees}
        pcwDuties={pcwDuties}
        newPcwEvaluation={newPcwEvaluation}
        setNewPcwEvaluation={setNewPcwEvaluation}
        saving={savingPcwEval}
        onSubmit={handleSavePcwEvaluation}
      />

      <RNEvaluationDialog
        open={rnEvalDialogOpen}
        onOpenChange={setRnEvalDialogOpen}
        employees={employees}
        rnDuties={rnDuties}
        rnGradingScale={rnGradingScale}
        newRnEvaluation={newRnEvaluation}
        setNewRnEvaluation={setNewRnEvaluation}
        saving={savingRnEval}
        onSubmit={handleSaveRnEvaluation}
      />
    </Layout>
  );
}
